<template>
    <div class="image-360-viewer">
        <div v-if="src" ref="viewer" class="image-360-viewer-canvas" :style="{height: canvasHeight}">
            <div v-if="src && !loading && !noScreenshot" class="screenshot-btn pnlm-orientation-button pnlm-controls pnlm-control" @click="screenshot">
                <i class="fas fa-camera"/>
            </div>
        </div>
        <div v-if="loading" class="image-360-viewer-loading">
            <i class="fa-solid fa-spinner fa-spin-pulse"/>
        </div>
    </div>
</template>

<script>
import "pannellum";
import "pannellum/build/pannellum.css";

export default {
    name: `image-360-viewer`,
    props: {
        src: {type: String, default: null},
        config: {
            type: Object,
            default: () => {
                return {};
            }
        },
        canvasHeight: {type: String, default: `78dvh`},
        noScreenshot: {type: Boolean, default: false},
        screenshotFilename: {type: String, default: `360-viewer-screenshot`}
    },
    data() {
        return {
            viewerInstance: null,
            loading: false
        }
    },
    mounted() {
        this.initializeViewer();
    },
    beforeDestroy() {
        this.destroyViewer();
    },
    methods: {
        getPannellum() {
            return window.pannellum;
        },
        initializeViewer() {
            if (!this.$refs.viewer || !this.src) {
                this.destroyViewer();
                return;
            }

            this.destroyViewer();

            const pannellumApi = this.getPannellum();
            this.loading = true;

            const defaultConfig = {
                type: `equirectangular`,
                panorama: this.src,
                autoLoad: true,
                showControls: true,
                yaw: 0,
                pitch: 0,
                hfov: 80,
                minHfov: 50,
                maxHfov: 120,
                mouseZoom: true,
                keyboardZoom: false,
                compass: true,
                northOffset: 0
            };

            this.viewerInstance = pannellumApi.viewer(this.$refs.viewer, {
                ...defaultConfig,
                ...this.config,
                type: `equirectangular`,
                panorama: this.src
            });

            this.$emit(`viewer-instance`, this.viewerInstance);

            this.viewerInstance.on(`load`, () => {
                this.loading = false;
                this.viewerInstance.resize();
                this.$emit(`viewer-loaded`, this.viewerInstance);
            });

            const pannellumMsgSpan = document.querySelector(`.pnlm-about-msg`);
            if (pannellumMsgSpan) {
                pannellumMsgSpan.remove();
            }
        },
        destroyViewer() {
            if (this.viewerInstance) {
                this.$emit(`viewer-instance`, null);
                this.viewerInstance.destroy();
                this.viewerInstance = null;
            }

            this.loading = false;
        },
        async screenshot() {
            const viewer = this.viewerInstance;
            const renderer = viewer.getRenderer();

            const pitch = viewer.getPitch() * Math.PI / 180;
            const yaw = viewer.getYaw() * Math.PI / 180;
            const hfov = viewer.getHfov() * Math.PI / 180;

            const container = viewer.getContainer();
            const containerRect = container.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const containerHeight = containerRect.height;
            const ratio = containerWidth / containerHeight;

            const oldWidth = container.style.width;
            const oldHeight = container.style.height;
            const oldPosition = container.style.position;
            const oldLeft = container.style.left;
            const oldTop = container.style.top;
            const oldZIndex = container.style.zIndex;

            try {
                // Temporarily enlarge the viewer
                container.style.position = `fixed`;
                container.style.left = `-10000px`;
                container.style.top = `0`;
                container.style.zIndex = `-1`;
                container.style.width = `1920px`;
                container.style.height = `${1920 / ratio}px`;

                // Force Pannellum to resize its internal canvas
                viewer.resize();

                // Render at the new drawing buffer size
                const dataUrl = renderer.render(pitch, yaw, hfov, {
                    returnImage: `image/png`
                });

                const a = document.createElement(`a`);
                a.href = dataUrl;
                a.download = `${this.screenshotFilename}.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } finally {
                // Restore viewer
                container.style.width = oldWidth;
                container.style.height = oldHeight;
                container.style.position = oldPosition;
                container.style.left = oldLeft;
                container.style.top = oldTop;
                container.style.zIndex = oldZIndex;

                viewer.resize();
            }
        }
    },
    watch: {
        src() {
            this.initializeViewer();
        }
    }
}
</script>

<style lang="scss" scoped>
.image-360-viewer {
    position: relative;
    background: #000;
}

.image-360-viewer-canvas {
    width: 100%;
}

.image-360-viewer-loading {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    background: rgba(0, 0, 0, 0.35);
    font-size: 32px;
    pointer-events: none;
}

:deep(.pnlm-about-msg) {
    display: none;
}

.screenshot-btn {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 90px;
    left: 4px;
    color: black;
}

</style>
